<template>
  <div class="form-group">
    <label>{{ $t(label) }}</label>
    <div class="input-group">
      <input type="text" class="calendar form-input" :id="fname" :name="fname" />
      <span class="input-group-btn">
        <button class="btn btn-primary pickerToggler" type="button" @click="togglePicker()">
          <Icon icon="calendar" class="icon" />
        </button>
      </span>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import Flatpickr from 'flatpickr';
import translationDe from 'flatpickr/dist/l10n/de';
import translationFr from 'flatpickr/dist/l10n/fr';
import moment from 'moment-mini';
import Icon from '../Icon.vue';

const { de } = translationDe;
const { fr } = translationFr;
const calendarTranslations = {
  de,
  fr,
};

import 'flatpickr/dist/themes/airbnb.css';

export default {
  name: 'DatePicker',
  components: {
    Icon,
  },
  props: {
    fname: { default: 'calendar' },
    label: { default: '' },
    availableDates: { default: () => [] },
    allDays: { default: () => false },
    except: { default: () => [] },
    modelValue: { default: '' },
    positionElement: { default: null },
    limits: { default: () => ({}) },
    soldOutDates: { default: () => ({}) },
    mode: { default: 'single' },
  },
  data: () => ({
    picker: null,
    previousDate: null,
  }),
  emits: ['update:modelValue', 'blur', 'dateChange'],
  computed: {
    ...mapState({
      language: (state) => state.language,
    }),
  },
  mounted() {
    this.createCalendar();
  },
  beforeUnmount() {
    //this.picker.destroy();
  },
  methods: {
    togglePicker() {
      setTimeout(() => {
        if (typeof this.picker !== 'undefined') {
          this.picker.toggle();
        }
      }, 1);
    },
    createCalendar() {
      {
        const self = this;

        if (this.language !== 'en') {
          Flatpickr.localize(calendarTranslations[this.language]);
        }

        let minDate;
        if (typeof this.limits.first !== 'undefined') {
          if (this.limits.first !== null) {
            const splitDate = this.limits.first.split(' ')[0].split('-');
            minDate = `${splitDate[0]}-${splitDate[1]}`;
          }
        }
        let maxDate;
        if (typeof this.limits.last !== 'undefined' && this.limits.last !== null) {
          const splitDate = this.limits.last.split(' ')[0].split('-');
          maxDate = `${splitDate[0]}-${splitDate[1]}-${splitDate[2]}`;
        }
        const pEl = document.querySelector(this.positionElement);
        this.picker = new Flatpickr(document.querySelector('.calendar'), {
          // eslint-disable-line
          // inline: true,
          mode: this.mode,
          enable: [() => true],
          positionElement: pEl,
          dateFormat: 'Y-m-d',
          altInput: true,
          altFormat: self.$t('DATE_FORMAT'),
          minDate,
          maxDate,
          onDayCreate: (dObj, dStr, fp, dayElem) => {
            const dayStr = moment(dayElem.dateObj).format('YYYY-MM-DD');
            const calendarDay = moment(dayElem.dateObj);
            const today = moment();
            if (calendarDay.isBefore(today)) {
              dayElem.classList.add('inThePast');
            }
            if (typeof this.soldOutDates[dayStr] !== 'undefined') {
              if (this.soldOutDates[dayStr] === true) {
                dayElem.classList.add('soldOutDay');
                dayElem.setAttribute('title', this.$t('DATE_PICKER_SOLDOUT_DAY'));
              }
            }
            if (
              this.availableDates.includes(dayStr) ||
              (this.allDays &&
                calendarDay.isSameOrAfter(today, 'day') &&
                !this.except.includes(dayStr))
            ) {
              dayElem.classList.add('availableDay');
            } else {
              dayElem.classList.add('unavailableDay');
              if (this.soldOutDates[dayStr] !== true) {
                if (this.$t) {
                  dayElem.setAttribute('title', this.$t('DATE_PICKER_UNAVAILABLE_DAY'));
                }
              }
            }
          },
          onOpen: (selectedDates) => {
            let dateStrStart = '';
            if (typeof selectedDates[0] !== 'undefined') {
              dateStrStart = moment(selectedDates[0]).format('YYYY-MM-DD');
            }
            this.previousDate = dateStrStart;
          },
          onChange: (selectedDates) => {
            const dateStrStart = moment(selectedDates[0]).format('YYYY-MM-DD');
            let dateStrEnd = null;

            if (typeof selectedDates[1] !== 'undefined') {
              dateStrEnd = moment(selectedDates[1]).format('YYYY-MM-DD');
            }

            if (
              (this.soldOutDates[dateStrStart] || !this.availableDates.includes(dateStrStart)) &&
              !this.allDays
            ) {
              this.$nextTick(() => {
                this.picker.setDate(this.previousDate, false);
                this.picker.open();
              });
              return;
            }

            if (dateStrStart !== '' && dateStrEnd !== null) {
              self.$emit('update:modelValue', dateStrStart + ' ' + dateStrEnd);
            } else if (dateStrStart !== '' && this.mode === 'single') {
              self.$emit('update:modelValue', dateStrStart);
            } else if (this.modelValue !== '') {
              // this.picker.setDate(this.modelValue);
              if (this.mode === 'single') {
                this.$nextTick(() => {
                  this.picker.jumpToDate(this.modelValue);
                });
              }
            }
            this.previousDate = dateStrStart;
          },
          onMonthChange: (selectedDates, dateStr, instance) => {
            let day = instance.selectedDates.length > 0 ? instance.selectedDates[0].getDate() : 1;
            const lastDayofMonth = new Date(
              instance.currentYear,
              instance.currentMonth + 1,
              0
            ).getDate(); // gets the last day of month
            if (day > lastDayofMonth) {
              day = lastDayofMonth;
            }
            self.$emit('dateChange', {
              year: instance.currentYear,
              month: instance.currentMonth,
              day,
            });
            // Months are 0 indexed
            return true;
          },
          onYearChange: (selectedDates, dateStr, instance) => {
            self.$emit('dateChange', {
              year: instance.currentYear,
              month: instance.currentMonth,
              day: instance.selectedDates.length > 0 ? instance.selectedDates[0].getDate() : 1,
            });
            // Months are 0 indexed
            return true;
          },
        });
        if (typeof this.modelValue !== 'undefined') {
          this.picker.setDate(this.modelValue);
          /* setTimeout(() => {
        this.picker.jumpToDate(this.modelValue);
      }, 1); */
        }
      }
    },
  },
  watch: {
    except() {
      this.createCalendar();
    },

    availableDates() {
      // this.picker.clear();
    },
    modelValue(newVal) {
      if (newVal !== '') {
        this.picker.setDate(newVal);
        setTimeout(() => {
          this.picker.setDate(newVal);
          this.previousDate = newVal;
        }, 1000);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../scss/form';
input {
  @extend .form-control;
}
</style>
<style lang="scss">
input {
  font-weight: bold;
}
.flatpickr-months,
.flatpickr-months .flatpickr-month {
  min-height: 34px !important;
  overflow: visible !important;
}

.flatpickr-day.availableDay {
  background-color: $brand-lighter;
}
.flatpickr-day.flatpickr-disabled {
  background-color: white;
}
@media screen and (max-width: $screen-xs) {
  .flatpickr-calendar {
    left: 0 !important;
    right: 0 !important;
    margin-left: auto;
    margin-right: auto;
  }
}
/*
.flatpickr-day.availableDay::before {
content: "";
width: 24px;
height: 24px;
background-color: rgba(0,0,255, 0.5);
display: block;
position: absolute;
margin: 9px;
border-radius: 50%;
}
 */

.soldOutDay {
  color: $brand-danger !important;
  cursor: not-allowed !important;
}

.soldOutDay.inThePast {
  color: rgba($text-color, 0.3) !important;
}

.unavailableDay {
  color: rgba($text-color, 0.3);
  cursor: not-allowed !important;
}
.unavailableDay.inThePast {
  color: rgba($text-color, 0.3);
}

.dayContainer .flatpickr-day.startRange {
  border-top-left-radius: 100px !important;
  border-bottom-left-radius: 100px !important;
  background-color: darken($brand-lighter, 20%);
  border-width: 0 !important;
}

.dayContainer .flatpickr-day.inRange {
  background-color: darken($brand-lighter, 20%);
  border-width: 0 !important;
  box-shadow: none;
}

.dayContainer .flatpickr-day.endRange {
  border-top-right-radius: 100px !important;
  border-bottom-right-radius: 100px !important;
  background-color: darken($brand-lighter, 20%);
  border-width: 0 !important;
}

.flatpickr-day.selected.startRange + .endRange:not(:nth-child(7n + 1)),
.flatpickr-day.startRange.startRange + .endRange:not(:nth-child(7n + 1)),
.flatpickr-day.endRange.startRange + .endRange:not(:nth-child(7n + 1)) {
  box-shadow: none;
}
</style>
